import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import './App.css';

const API_BASE = process.env.REACT_APP_API_BASE
  || (window.location.hostname === 'localhost' ? 'http://localhost:8080' : '');

const REGIONS = {
  seoul: '\uC11C\uC6B8',
  busan: '\uBD80\uC0B0',
  daegu: '\uB300\uAD6C',
  incheon: '\uC778\uCC9C',
  gwangju: '\uAD11\uC8FC',
  daejeon: '\uB300\uC804',
  ulsan: '\uC6B8\uC0B0',
  sejong: '\uC138\uC885',
  gyeonggi: '\uACBD\uAE30',
  gangwon: '\uAC15\uC6D0',
  chungbuk: '\uCDA9\uBD81',
  chungnam: '\uCDA9\uB0A8',
  jeonbuk: '\uC804\uBD81',
  jeonnam: '\uC804\uB0A8',
  gyeongbuk: '\uACBD\uBD81',
  gyeongnam: '\uACBD\uB0A8',
  jeju: '\uC81C\uC8FC',
};

const THEMES = {
  food: {
    label: '\uBA39\uAC70\uB9AC \uCD95\uC81C',
    keywords: ['\uBA39\uAC70\uB9AC', '\uC74C\uC2DD', '\uD478\uB4DC', '\uC2DD\uD488', '\uB9E5\uC8FC', '\uC640\uC778', '\uCEE4\uD53C', '\uB9DB', '\uD2B9\uC0B0\uBB3C'],
  },
  music: {
    label: '\uC74C\uC545 \uCD95\uC81C',
    keywords: ['\uC74C\uC545', '\uBBA4\uC9C1', '\uCF58\uC11C\uD2B8', '\uC7AC\uC988', '\uB85D', '\uB77D', '\uAC00\uC694', '\uC624\uD398\uB77C'],
  },
  flower: {
    label: '\uAF43 \uCD95\uC81C',
    keywords: ['\uAF43', '\uBC9A\uAF43', '\uC7A5\uBBF8', '\uD29C\uB9BD', '\uAD6D\uD654', '\uB9E4\uD654', '\uC720\uCC44', '\uC5F0\uAF43'],
  },
  night: {
    label: '\uC57C\uAC04 \uCD95\uC81C',
    keywords: ['\uC57C\uAC04', '\uBC24', '\uB098\uC774\uD2B8', '\uBD88\uBE5B', '\uBE5B', '\uB77C\uC774\uD2B8', '\uB4F1\uBD88'],
  },
  family: {
    label: '\uAC00\uC871 \uCD95\uC81C',
    keywords: ['\uAC00\uC871', '\uC5B4\uB9B0\uC774', '\uC544\uC774', '\uCCB4\uD5D8', '\uB180\uC774', '\uD0A4\uC988'],
  },
  free: {
    label: '\uBB34\uB8CC \uCD95\uC81C',
    keywords: ['\uBB34\uB8CC'],
  },
};

function getRoute() {
  const path = window.location.pathname.replace(/\/index\.html$/, '').replace(/\/$/, '') || '/';
  const parts = path.split('/').filter(Boolean);
  if (parts[0] !== 'festivals') return {};
  if (parts[1] === 'month') return { month: normalizeMonth(parts[2]) };
  if (REGIONS[parts[1]] && parts[2] === 'month') {
    return { regionSlug: parts[1], month: normalizeMonth(parts[3]) };
  }
  if (REGIONS[parts[1]]) return { regionSlug: parts[1] };
  if (THEMES[parts[1]]) return { themeSlug: parts[1] };
  return {};
}

function normalizeMonth(value) {
  const month = Math.max(1, Math.min(12, Number(value || new Date().getMonth() + 1)));
  return String(month).padStart(2, '0');
}

function monthRange(month) {
  const year = new Date().getFullYear();
  const m = Number(month);
  const start = `${year}${String(m).padStart(2, '0')}01`;
  const endDate = new Date(year, m, 0);
  const end = `${year}${String(m).padStart(2, '0')}${String(endDate.getDate()).padStart(2, '0')}`;
  return { start, end, year };
}

function getPageMeta(filters) {
  const parts = [];
  if (filters.month) parts.push(`${Number(filters.month)}\uC6D4`);
  if (filters.regionSlug) parts.push(REGIONS[filters.regionSlug]);
  if (filters.themeSlug) parts.push(THEMES[filters.themeSlug].label);

  const hasTheme = Boolean(filters.themeSlug);
  const title = parts.length
    ? hasTheme ? parts.join(' ') : `${parts.join(' ')} \uCD95\uC81C`
    : '\uC804\uAD6D \uCD95\uC81C';
  const lead = parts.length
    ? `${title}\uB97C \uAE30\uAC04, \uC9C0\uC5ED, \uC7A5\uC18C\uBCC4\uB85C \uC815\uB9AC\uD588\uC2B5\uB2C8\uB2E4.`
    : '\uC804\uAD6D\uC5D0\uC11C \uC5F4\uB9AC\uB294 \uCD95\uC81C\uB97C \uC6D4\uBCC4, \uC9C0\uC5ED\uBCC4, \uD14C\uB9C8\uBCC4\uB85C \uCC3E\uC544\uBCFC \uC218 \uC788\uB294 PlayPin \uCD95\uC81C \uBAA9\uB85D\uC785\uB2C8\uB2E4.';
  const pageTitle = `${title} 일정 | PlayPin`;
  const description = `${lead} PlayPin에서 전국 축제와 행사 일정을 지도와 목록으로 확인하세요.`;
  return { title, lead, pageTitle, description };
}

function SeoFestivalPages() {
  const route = useMemo(getRoute, []);
  const [selectedRegion, setSelectedRegion] = useState(route.regionSlug || '');
  const [selectedMonth, setSelectedMonth] = useState(route.month || '');
  const [selectedTheme, setSelectedTheme] = useState(route.themeSlug || '');
  const [items, setItems] = useState([]);
  const [expandedKey, setExpandedKey] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const filters = useMemo(() => ({
    regionSlug: selectedRegion,
    month: selectedMonth,
    themeSlug: selectedTheme,
  }), [selectedRegion, selectedMonth, selectedTheme]);
  const meta = useMemo(() => getPageMeta(filters), [filters]);
  const visibleItems = items.slice(0, 60);
  const gridItems = useMemo(() => orderItemsForExpandedGrid(visibleItems, expandedKey), [visibleItems, expandedKey]);

  useEffect(() => {
    updateDocumentMeta({
      title: meta.pageTitle,
      description: meta.description,
      canonicalPath: canonicalPath(filters),
    });
  }, [meta, filters]);

  useEffect(() => {
    const fetchItems = async () => {
      setLoading(true);
      setError('');
      try {
        const params = { type: 'EV01' };
        if (selectedRegion) params.region = REGIONS[selectedRegion];
        if (selectedMonth) {
          const range = monthRange(selectedMonth);
          params.startDate = range.start;
          params.endDate = range.end;
        } else {
          params.startDate = compactDate(new Date());
          params.endDate = compactDate(addMonths(new Date(), 12));
        }
        const { data } = await axios.get(`${API_BASE}/api/festivals`, { params });
        setItems(filterTheme(data || [], selectedTheme));
      } catch (e) {
        setError('\uCD95\uC81C \uBAA9\uB85D\uC744 \uBD88\uB7EC\uC624\uC9C0 \uBABB\uD588\uC2B5\uB2C8\uB2E4.');
      } finally {
        setLoading(false);
      }
    };
    fetchItems();
  }, [selectedRegion, selectedMonth, selectedTheme]);

  return (
    <main className="seo-page">
      <div className="seo-shell">
        <div className="seo-main-column">
          <nav className="seo-nav">
            <a href="/" className="seo-brand-logo">PlayPin</a>
            <span />
            <a href="/festivals">{'\uC804\uAD6D \uCD95\uC81C'}</a>
            <a href="/sources">{'\uB370\uC774\uD130 \uCD9C\uCC98'}</a>
          </nav>

          <header className="seo-hero">
            <p>{'PlayPin \uCD95\uC81C \uAC00\uC774\uB4DC'}</p>
            <h1>{meta.title}</h1>
            <strong>{meta.lead}</strong>
            <div className="seo-hero-actions">
              <a href={mapUrl(filters)}>{'\uC9C0\uB3C4\uC5D0\uC11C \uBCF4\uAE30'}</a>
              <button type="button" onClick={() => { setSelectedRegion(''); setSelectedMonth(''); setSelectedTheme(''); }}>
                {'\uC870\uAC74 \uCD08\uAE30\uD654'}
              </button>
            </div>
          </header>

          <FilterBand title={'\uC9C0\uC5ED'} allLabel={'\uC804\uAD6D'} value={selectedRegion} options={Object.entries(REGIONS).map(([value, label]) => ({ value, label }))} onChange={setSelectedRegion} />
          <FilterBand
            title={'\uC6D4'}
            allLabel={'\uC804\uCCB4 \uC6D4'}
            value={selectedMonth}
            options={Array.from({ length: 12 }, (_, index) => {
              const month = String(index + 1).padStart(2, '0');
              return { value: month, label: `${index + 1}\uC6D4` };
            })}
            onChange={setSelectedMonth}
          />
          <FilterBand title={'\uD14C\uB9C8'} allLabel={'\uC804\uCCB4 \uD14C\uB9C8'} value={selectedTheme} options={Object.entries(THEMES).map(([value, theme]) => ({ value, label: theme.label }))} onChange={setSelectedTheme} />
          <section className="seo-filter-help">
            {'\uD14C\uB9C8\uB294 \uBCC4\uB3C4 \uD0DC\uADF8\uAC00 \uC544\uB2C8\uB77C \uCD95\uC81C\uBA85, \uC18C\uAC1C, \uD504\uB85C\uADF8\uB7A8, \uC7A5\uC18C, \uC694\uAE08 \uC815\uBCF4\uC758 \uD0A4\uC6CC\uB4DC\uB97C \uAE30\uC900\uC73C\uB85C \uBD84\uB958\uD569\uB2C8\uB2E4.'}
          </section>

          <section className="seo-results">
            <div className="seo-section-head">
              <h2>{meta.title} {'\uBAA9\uB85D'}</h2>
              <span>{loading ? '\uBD88\uB7EC\uC624\uB294 \uC911' : `${items.length.toLocaleString()}\uAC74`}</span>
            </div>
            {error && <div className="seo-empty">{error}</div>}
            {!error && !loading && items.length === 0 && <div className="seo-empty">{'\uC870\uAC74\uC5D0 \uB9DE\uB294 \uCD95\uC81C\uAC00 \uC5C6\uC2B5\uB2C8\uB2E4.'}</div>}
            <div className="seo-card-grid">
              {gridItems.map(({ item, originalIndex }) => {
                const key = item.contentId || item.title;
                return <FestivalSeoCard key={key} item={item} index={originalIndex} expanded={expandedKey === key} onToggle={() => setExpandedKey(expandedKey === key ? '' : key)} />;
              })}
            </div>
          </section>

          <section className="seo-note">
            <h2>{'\uC774\uC6A9 \uC804 \uD655\uC778'}</h2>
            <p>{'\uCD95\uC81C \uC77C\uC815, \uC7A5\uC18C, \uC694\uAE08, \uD648\uD398\uC774\uC9C0 \uC815\uBCF4\uB294 \uC6D0 \uC81C\uACF5\uAE30\uAD00 \uC0AC\uC815\uC5D0 \uB530\uB77C \uBCC0\uACBD\uB420 \uC218 \uC788\uC2B5\uB2C8\uB2E4. \uBC29\uBB38 \uC804 \uACF5\uC2DD \uD648\uD398\uC774\uC9C0 \uB610\uB294 \uC8FC\uCD5C\uAE30\uAD00 \uC548\uB0B4\uB97C \uD655\uC778\uD558\uC138\uC694.'}</p>
          </section>
        </div>
        <aside className="seo-side-ad"><AdSlot label="300x600" /></aside>
        <div className="seo-bottom-ad"><AdSlot label="Responsive AD" /></div>
      </div>
    </main>
  );
}

function FilterBand({ title, allLabel, value, options, onChange }) {
  return (
    <section className="seo-filter-band" aria-label={title}>
      <h2>{title}</h2>
      <div>
        <button type="button" className={!value ? 'active' : ''} onClick={() => onChange('')}>{allLabel}</button>
        {options.map(option => (
          <button
            key={option.value}
            type="button"
            className={value === option.value ? 'active' : ''}
            onClick={() => onChange(value === option.value ? '' : option.value)}
          >
            {option.label}
          </button>
        ))}
      </div>
    </section>
  );
}

function orderItemsForExpandedGrid(items, expandedKey) {
  const rows = items.map((item, originalIndex) => ({ item, originalIndex }));
  const expandedIndex = rows.findIndex(row => (row.item.contentId || row.item.title) === expandedKey);
  if (expandedIndex > 0 && expandedIndex % 3 === 2) {
    const copy = [...rows];
    const previous = copy[expandedIndex - 1];
    copy[expandedIndex - 1] = copy[expandedIndex];
    copy[expandedIndex] = previous;
    return copy;
  }
  return rows;
}

function FestivalSeoCard({ item, index, expanded, onToggle }) {
  const area = item.addr1 ? item.addr1.split(' ').slice(0, 2).join(' ') : item.eventplace || '';
  const expandClass = index % 3 === 2 ? 'seo-card-expand-left' : 'seo-card-expand-right';
  return (
    <article className={`seo-card ${expanded ? `seo-card-expanded ${expandClass}` : ''}`}>
      {item.firstimage && <img src={item.firstimage} alt={item.title} />}
      <div className="seo-card-body">
        <p>{area || '\uC804\uAD6D'}</p>
        <h3>{item.title}</h3>
        <dl>
          <div><dt>{'\uAE30\uAC04'}</dt><dd>{formatDate(item.eventStartDate)} ~ {formatDate(item.eventEndDate)}</dd></div>
          {item.eventplace && <div><dt>{'\uC7A5\uC18C'}</dt><dd>{item.eventplace}</dd></div>}
        </dl>
        {item.overview && <span>{expanded ? stripHtml(item.overview) : `${stripHtml(item.overview).slice(0, 120)}${stripHtml(item.overview).length > 120 ? '...' : ''}`}</span>}
        <div className="seo-card-actions">
          <a href={mapUrlForItem(item)}>{'\uC9C0\uB3C4\uC5D0\uC11C \uBCF4\uAE30'}</a>
          <button type="button" onClick={onToggle}>{expanded ? '\uC0C1\uC138 \uB2EB\uAE30' : '\uC0C1\uC138\uC815\uBCF4'}</button>
        </div>
        {expanded && <FestivalExpandedInfo item={item} />}
      </div>
    </article>
  );
}

function FestivalExpandedInfo({ item }) {
  const rows = [
    ['\uAE30\uAC04', `${formatDate(item.eventStartDate)} ~ ${formatDate(item.eventEndDate)}`],
    ['\uC8FC\uC18C', [item.addr1, item.addr2].filter(Boolean).join(' ')],
    ['\uC7A5\uC18C', item.eventplace],
    ['\uC2DC\uAC04', item.playtime],
    ['\uC694\uAE08', item.usetimefestival],
    ['\uC8FC\uCD5C', item.sponsor1],
    ['\uC804\uD654', item.tel || item.sponsor1tel],
  ].filter(([, value]) => value);

  return (
    <div className="seo-card-detail">
      <dl>
        {rows.map(([label, value]) => (
          <div key={label}><dt>{label}</dt><dd>{value}</dd></div>
        ))}
      </dl>
      {item.program && (
        <section>
          <h4>{'\uD504\uB85C\uADF8\uB7A8'}</h4>
          <p>{stripHtml(item.program)}</p>
        </section>
      )}
      {item.homepage && (
        <section>
          <h4>{'\uD648\uD398\uC774\uC9C0'}</h4>
          <LinkText value={item.homepage} />
        </section>
      )}
      <section>
        <h4>{'\uCD9C\uCC98'}</h4>
        <p>{[item.sourceName, item.sourceCategory].filter(Boolean).join(' · ') || '\uD55C\uAD6D\uAD00\uAD11\uACF5\uC0AC TourAPI'}</p>
        {item.sourceUrl && <LinkText value={item.sourceUrl} />}
      </section>
    </div>
  );
}

function AdSlot({ label }) {
  return (
    <div className="seo-ad-slot">
      <span>AD</span>
      <strong>{label}</strong>
    </div>
  );
}

function filterTheme(items, themeSlug) {
  if (!themeSlug || !THEMES[themeSlug]) return items;
  const keywords = THEMES[themeSlug].keywords;
  return items.filter(item => {
    const text = [item.title, item.overview, item.program, item.eventplace, item.usetimefestival].filter(Boolean).join(' ');
    return keywords.some(keyword => text.includes(keyword));
  });
}

function mapUrl(filters) {
  const params = new URLSearchParams({ type: 'EV01' });
  if (filters.regionSlug) params.set('region', REGIONS[filters.regionSlug]);
  if (filters.month) {
    const range = monthRange(filters.month);
    params.set('startDate', `${range.start.slice(0, 4)}-${range.start.slice(4, 6)}-${range.start.slice(6, 8)}`);
    params.set('endDate', `${range.end.slice(0, 4)}-${range.end.slice(4, 6)}-${range.end.slice(6, 8)}`);
  }
  return `/?${params.toString()}`;
}

function mapUrlForItem(item) {
  const params = new URLSearchParams({ type: 'EV01' });
  if (item.title) params.set('keyword', item.title);
  if (item.addr1) params.set('region', item.addr1.split(' ')[0]);
  if (item.eventStartDate) params.set('startDate', dashedDate(item.eventStartDate));
  if (item.eventEndDate) params.set('endDate', dashedDate(item.eventEndDate));
  return `/?${params.toString()}`;
}

function dashedDate(value) {
  const s = String(value || '').replace(/-/g, '');
  if (s.length !== 8) return value;
  return `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}`;
}

function compactDate(date) {
  return `${date.getFullYear()}${String(date.getMonth() + 1).padStart(2, '0')}${String(date.getDate()).padStart(2, '0')}`;
}

function addMonths(date, count) {
  const next = new Date(date);
  next.setMonth(next.getMonth() + count);
  return next;
}

function formatDate(value) {
  if (!value) return '-';
  const s = String(value).replace(/-/g, '');
  if (s.length !== 8) return value;
  return `${s.slice(0, 4)}.${s.slice(4, 6)}.${s.slice(6, 8)}`;
}

function stripHtml(value) {
  return String(value || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
}

function LinkText({ value }) {
  const text = stripHtml(value);
  const urlPattern = /(https?:\/\/[^\s<>"']+|www\.[^\s<>"']+)/gi;
  const urls = text.match(urlPattern);
  if (!urls) return <p>{text}</p>;
  const parts = text.split(urlPattern);
  return (
    <p>
      {parts.map((part, index) => {
        if (!part) return null;
        if (part.match(urlPattern)) {
          const href = normalizeExternalUrl(part);
          return <a key={index} href={href} target="_blank" rel="noreferrer">{part}</a>;
        }
        return <span key={index}>{part}</span>;
      })}
    </p>
  );
}

function normalizeExternalUrl(value) {
  const text = stripHtml(value);
  if (!text) return '#';
  const first = text.split(/\s+/)[0];
  if (/^https?:\/\//i.test(first)) return first;
  if (/^\/\//.test(first)) return `https:${first}`;
  if (/^(www\.|[a-z0-9-]+\.[a-z]{2,})/i.test(first)) return `https://${first}`;
  return first;
}

function canonicalPath(filters) {
  if (filters.regionSlug && filters.month) return `/festivals/${filters.regionSlug}/month/${filters.month}`;
  if (filters.regionSlug) return `/festivals/${filters.regionSlug}`;
  if (filters.month) return `/festivals/month/${filters.month}`;
  if (filters.themeSlug) return `/festivals/${filters.themeSlug}`;
  return '/festivals';
}

function updateDocumentMeta({ title, description, canonicalPath }) {
  const canonicalUrl = `https://playpin.kr${canonicalPath}`;
  document.title = title;
  setMeta('name', 'description', description);
  setMeta('property', 'og:title', title);
  setMeta('property', 'og:description', description);
  setMeta('property', 'og:url', canonicalUrl);
  setCanonical(canonicalUrl);
}

function setMeta(attribute, key, content) {
  let el = document.head.querySelector(`meta[${attribute}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attribute, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function setCanonical(href) {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

export default SeoFestivalPages;
